import React, { useEffect, useState, useCallback } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { useDispatch, useSelector } from "react-redux";

import { subscribeToProduct } from "../../../api/apiEndpoints";
import PaymentForm from "../PaymentForm";
import DiscountCodeInput from "../../../components/DiscountCodeInput";
import { CircularProgress } from "@mui/material";
import { setSubscriptionId, setClientSecretStripe, clearAppliedDiscount, clearClientSecretStripe, clearSubscriptionId } from "../../../slices/appSlice"; 

type RenewalTier = { key: 'monthly' | 'quarterly' | 'yearly'; label: string; rate: number; term: string; note: string; savings: number | null; businessProductId: string };

const Payment = () => {
  // Track all state locally to avoid Redux issues
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);
  const clientSecret = useSelector((state: any) => state.appData.clientSecretStripe);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [elementsKey, setElementsKey] = useState(Date.now());
  const [hasTriedSubscription, setHasTriedSubscription] = useState<boolean>(false);
  const [isTrialSubscription, setIsTrialSubscription] = useState<boolean>(false);
  const [selectedRenewalTier, setSelectedRenewalTier] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');
  const dispatch = useDispatch();
  const [isRetrying, setIsRetrying] = useState(false);

  // Get needed data from Redux
  const { selectedProduct, appliedDiscount, basketProduct } = useSelector((state: any) => state.appData);
  const { idToken } = useSelector((state: any) => state.auth);

  useEffect(() => {
    // This function runs when the component unmounts
    return () => {
      dispatch(clearClientSecretStripe());
      dispatch(clearSubscriptionId());
      dispatch(clearAppliedDiscount());
    };
  }, [dispatch]);

  useEffect(() => {
    if (clientSecret) {
      setIsLoading(false);
    }
  }, [clientSecret]);

  // Load Stripe on component mount
  useEffect(() => {
    const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (stripeKey) {
      setStripePromise(loadStripe(stripeKey));
    }
  }, []);

  useEffect(() => {
    if (clientSecret) {
      setElementsKey(Date.now());
    }
  }, [clientSecret]);

  // Log when discount changes
  useEffect(() => {
  }, [appliedDiscount]);

  useEffect(() => {
    dispatch(clearAppliedDiscount());
  }, []);

  const createSubscription = useCallback(async () => {
    if (idToken && selectedProduct?.businessProductId && !clientSecret) {
      try {
        setIsLoading(true);
        setError(null);
  
        const response = await subscribeToProduct(
          selectedProduct.businessProductId,
          appliedDiscount?.stripePromoId ?? null
        );
  
        if (response?.isTrialSubscription) {
          setIsTrialSubscription(true);
        }
  
        if (response?.clientSecret) {
          dispatch(setSubscriptionId(response.stripeSubscriptionId));
          dispatch(setClientSecretStripe(response.clientSecret));
        } else {
          setError("Could not process payment. Please try again.");
        }
      } catch (err) {
        // console.error("Subscription error:", err);
        setError("Payment setup failed. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }
  }, [idToken, selectedProduct, clientSecret, appliedDiscount, dispatch]);  

  // One-time subscription attempt using a ref to prevent loops
  useEffect(() => {
    createSubscription();
  }, [createSubscription]);
  
  // Derive available renewal tiers from the selected gym's premium products
  const gymProducts: any[] = basketProduct?.products ?? [];
  const isActiveProduct = (p: any) => p?.is_active !== false && p?.isActive !== false;
  const monthlyPremium = gymProducts.find((p: any) => p.name === 'premium' && isActiveProduct(p));
  const quarterlyPremium = gymProducts.find((p: any) => p.name === 'premium_quarterly' && isActiveProduct(p));
  const yearlyPremium = gymProducts.find((p: any) => p.name === 'premium_yearly' && isActiveProduct(p));

  const renewalTiers: RenewalTier[] = [
    ...(monthlyPremium ? [{ key: 'monthly' as const, label: 'Monthly', rate: monthlyPremium.price?.preTaxCost ?? monthlyPremium.preTaxCost ?? 0, term: '/month', note: 'Billed monthly', savings: null, businessProductId: monthlyPremium.businessProductId }] : []),
    ...(quarterlyPremium ? [{ key: 'quarterly' as const, label: 'Quarterly', rate: (quarterlyPremium.price?.preTaxCost ?? quarterlyPremium.preTaxCost ?? 0) * 0.90, term: '/quarter', note: 'Billed every 3 months', savings: 10, businessProductId: quarterlyPremium.businessProductId }] : []),
    ...(yearlyPremium ? [{ key: 'yearly' as const, label: 'Annual', rate: (yearlyPremium.price?.preTaxCost ?? yearlyPremium.preTaxCost ?? 0) * 0.85, term: '/year', note: 'Billed annually', savings: 15, businessProductId: yearlyPremium.businessProductId }] : []),
  ];

  const hasMultipleTiers = renewalTiers.length > 1;
  const selectedTierObj = renewalTiers.find(t => t.key === selectedRenewalTier) ?? renewalTiers[0];
  const selectedTierProductId = selectedTierObj?.businessProductId;

  // Function to manually retry subscription
  const retrySubscription = () => {
    if (isRetrying) return;
      setIsRetrying(true);

      dispatch(clearAppliedDiscount());
      dispatch(clearClientSecretStripe());
      dispatch(clearSubscriptionId());
      setError(null);
      setIsTrialSubscription(false);

      setTimeout(() => {
        createSubscription().finally(() => setIsRetrying(false));
      }, 0);
  };
  
  
  return (
    <div>
      {isLoading && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <CircularProgress size={25} />
        </div>
      )}
      
      {error && !isLoading && (
        <div style={{ color: 'red', textAlign: 'center', padding: '20px' }}>
          {error}
          <div style={{ marginTop: '10px' }}>
            <button 
              onClick={retrySubscription}
              disabled={isRetrying}
              style={{
                padding: '8px 16px',
                backgroundColor: isRetrying ? '#999' : '#333',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isRetrying ? 'not-allowed' : 'pointer'
              }}
            >
              {isRetrying ? 'Retrying...' : 'Try Again'}
            </button>
          </div>
        </div>
      )}
      
      {/* Renewal tier selector — shown once Stripe is ready */}
      {hasMultipleTiers && !isLoading && !error && (
        <div style={{ marginBottom: '24px' }}>
          <p style={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '12px' }}>
            RENEWAL PLAN
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            {renewalTiers.map(tier => {
              const selected = selectedRenewalTier === tier.key;
              return (
                <div
                  key={tier.key}
                  onClick={() => setSelectedRenewalTier(tier.key)}
                  style={{
                    flex: 1, position: 'relative', borderRadius: '14px', border: `1.5px solid ${selected ? '#000' : '#e5e7eb'}`,
                    backgroundColor: selected ? '#f5f5f5' : '#fafafa', padding: '14px 10px',
                    textAlign: 'center', cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  {tier.savings && (
                    <div style={{ position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#000', borderRadius: '20px', padding: '3px 8px', whiteSpace: 'nowrap' }}>
                      <span style={{ color: '#fff', fontSize: '10px', fontWeight: 700 }}>Save {tier.savings}%</span>
                    </div>
                  )}
                  <p style={{ fontSize: '11px', fontWeight: 600, color: selected ? '#000' : '#999', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '4px 0 6px' }}>{tier.label}</p>
                  <p style={{ fontSize: '20px', fontWeight: 800, color: selected ? '#000' : '#bbb', margin: '0 0 4px' }}>${Math.round(tier.rate)}<span style={{ fontSize: '12px', fontWeight: 500 }}>{tier.term}</span></p>
                  <p style={{ fontSize: '11px', color: selected ? '#555' : '#bbb', margin: 0 }}>{tier.note}</p>
                </div>
              );
            })}
          </div>
          {selectedTierObj && (
            <p style={{ fontSize: '13px', color: '#555', lineHeight: '1.6', marginTop: '14px' }}>
              After your first month, your membership continues at <strong>${Math.round(selectedTierObj.rate)}{selectedTierObj.term}</strong> at <strong>{basketProduct?.name}</strong>, plus applicable taxes and fees. You can select a new trial or cancel anytime.
            </p>
          )}
        </div>
      )}

      {clientSecret && stripePromise && !isLoading && !error && (
        <Elements
          key={elementsKey}
          stripe={stripePromise}
          options={{
            clientSecret: clientSecret,
            appearance: {
              theme: 'flat',
              variables: {
                fontFamily: ' "Gill Sans", sans-serif',
                fontLineHeight: '1.5',
                borderRadius: '10px',
                colorBackground: '#F6F8FA',
                accessibleColorOnColorPrimary: '#262626'
              },
              rules: {
                '.Block': {
                  backgroundColor: 'var(--colorBackground)',
                  boxShadow: 'none',
                  padding: '12px'
                },
                '.Input': {
                  padding: '12px'
                },
                '.Input:disabled, .Input--invalid:disabled': {
                  color: 'lightgray'
                },
                '.Tab': {
                  padding: '10px 12px 8px 12px',
                  border: 'none'
                },
                '.Tab:hover': {
                  border: 'none',
                  boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)'
                },
                '.Tab--selected, .Tab--selected:focus, .Tab--selected:hover': {
                  border: 'none',
                  backgroundColor: '#fff',
                  boxShadow: '0 0 0 1.5px var(--colorPrimaryText), 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)'
                },
                '.Label': {
                  fontWeight: '500'
                }
              }
            },
          }}
        >
          <PaymentForm isTrialSubscription={isTrialSubscription} selectedTierProductId={selectedTierProductId} />
        </Elements>
      )}
      
      {!clientSecret && !isLoading && !error && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <CircularProgress size={25} />
        </div>
      )}
    </div>
  );
};

export default Payment;